import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import { listAdminUsers, updateAdminUserRole, deleteAdminUserRecord, setAdminUserClaims } from '../../api/adminUsersApi';
import './CreateAdmin.css';

const CreateAdmin = () => {
  const navigate = useNavigate();
  const { admin: currentAdmin } = useAdminAuth();
  const isSuperAdmin = currentAdmin?.role === 'super-admin';
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [settingClaims, setSettingClaims] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [editingRole, setEditingRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const adminsList = await listAdminUsers();
        setAdmins(adminsList);
      } catch (err) {
        console.error('Error fetching admins:', err);
        setError('Failed to load admin list');
      } finally {
        setLoadingAdmins(false);
      }
    };

    fetchAdmins();
  }, [success]);

  const getCardDisplayName = (admin) => {
    const firstName = admin.firstName || '';
    const lastName = admin.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    // Fallback to email if no name available
    return admin.email || 'Admin User';
  };

  const handleDelete = async (adminId, adminEmail) => {
    if (!isSuperAdmin) {
      setError("Only Super Admins can delete admin accounts");
      return;
    }

    if (adminEmail === currentAdmin?.email) {
      setError("You cannot delete your own account");
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${adminEmail}? This action cannot be undone.`)) {
      try {
        await deleteAdminUserRecord(adminId);

        const adminsList = await listAdminUsers();
        setAdmins(adminsList);
        
        const message = 'Admin account deleted successfully';
        setSuccess(message);
        setProfileModalOpen(false);
        setSelectedAdmin(null);
      } catch (err) {
        console.error('Error deleting admin:', err);
        setError(err.message || 'Failed to delete admin account');
      }
    }
  };


  const handleUpdateRole = async () => {
    if (!selectedAdmin) return;

    const adminToUpdate = admins.find(a => a.id === selectedAdmin.id);
    if (adminToUpdate?.role === 'super-admin') {
      setError('Super Admin role cannot be changed through the UI. It must be set directly in the database.');
      return;
    }
    
    if (editingRole === 'super-admin') {
      setError('Super Admin role cannot be set through the UI. It must be added directly to the database.');
      return;
    }

    try {
      const updated = await updateAdminUserRole(selectedAdmin.id, editingRole);
      const adminsList = await listAdminUsers();
      setAdmins(adminsList);
      setSelectedAdmin({ ...selectedAdmin, ...updated, role: editingRole });
      setSuccess('Role updated successfully');
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role');
    }
  };

  const handleSetAdminClaims = async () => {
    if (!selectedAdmin) return;

    try {
      setSettingClaims(true);
      setError('');
      
      await setAdminUserClaims(selectedAdmin.id);
      setSuccess(`Admin claims set successfully for ${selectedAdmin.email}. Please ask them to sign out and sign back in.`);
    } catch (err) {
      console.error('Error setting admin claims:', err);
      setError(err.message || 'Failed to set admin claims');
    } finally {
      setSettingClaims(false);
    }
  };

  const openProfile = async (admin) => {
    setSelectedAdmin(admin);
    setEditingRole(admin.role || 'admin');
    setProfileModalOpen(true);
    setError('');
    setSuccess('');
    setProfileLoading(true);
    setProfileData(null);

    // Profile fields live on the admin database row already loaded in the list.
    try {
      setProfileData(admin);
    } catch (err) {
      console.error('Error fetching admin profile:', err);
      setError('Failed to load admin profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const closeProfile = () => {
    setProfileModalOpen(false);
    setSelectedAdmin(null);
    setProfileData(null);
    setEditingRole('');
    setError('');
    setSuccess('');
  };

  const getAdminDisplayName = (admin) => {
    if (!admin) return '';
    const firstName = profileData?.firstName || admin.firstName || '';
    const lastName = profileData?.lastName || admin.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    return admin.email || 'Admin User';
  };

  const getInitials = (admin) => {
    if (!admin) return '?';
    const firstName = profileData?.firstName || admin.firstName || '';
    const lastName = profileData?.lastName || admin.lastName || '';
    const email = admin.email || '';
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName[0].toUpperCase();
    } else if (email) {
      const parts = email.split('@')[0].split('.');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return email.substring(0, 2).toUpperCase();
    }
    return 'A';
  };

  const getRoleDisplayName = (role) => {
    if (!role) return 'Admin';
    return role === 'super-admin' ? 'Super Admin' : role.charAt(0).toUpperCase() + role.slice(1);
  };

  const filteredAdmins = admins.filter(a => {
    const matchesRole = roleFilter === 'all' ? true : (a.role || 'admin') === roleFilter;
    const matchesSearch = searchQuery === '' || 
      a.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.lastName?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRole && matchesSearch;
  });

  return (
    <div className="admin-mgmt-page">
      {error && (
        <div className="admin-mgmt-alert admin-mgmt-alert-error">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="admin-mgmt-alert admin-mgmt-alert-success">
          <span>✓</span>
          <span>{success}</span>
        </div>
      )}

      <div className="admin-mgmt-controls">
        <div className="admin-mgmt-controls-left">
          <button
            className="admin-mgmt-invite-btn"
            onClick={() => navigate(adminPath(ADMIN_PATHS.ADMIN_MANAGEMENT_INVITE_CREATE))}
          >
          Invite to the Team
          </button>
          <div className="admin-mgmt-filter">
            <button
              className={`admin-mgmt-filter-btn ${roleFilter === 'all' ? 'active' : ''}`}
              onClick={() => setRoleFilter('all')}
            >
              All
            </button>
            <button
              className={`admin-mgmt-filter-btn ${roleFilter === 'admin' ? 'active' : ''}`}
              onClick={() => setRoleFilter('admin')}
            >
              Admin
            </button>
            <button
              className={`admin-mgmt-filter-btn ${roleFilter === 'support' ? 'active' : ''}`}
              onClick={() => setRoleFilter('support')}
            >
              Support
            </button>
            <button
              className={`admin-mgmt-filter-btn ${roleFilter === 'super-admin' ? 'active' : ''}`}
              onClick={() => setRoleFilter('super-admin')}
            >
              Super Admin
            </button>
          </div>
          <div className="admin-mgmt-search-wrapper">
            <input
              type="text"
              className="admin-mgmt-search"
              placeholder="Search admins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="admin-mgmt-count">
          {filteredAdmins.length} {filteredAdmins.length === 1 ? 'account' : 'accounts'}
        </div>
      </div>

      {loadingAdmins ? (
        <div className="admin-mgmt-loading">
          <div className="admin-mgmt-spinner"></div>
        </div>
      ) : (
        <div className="admin-mgmt-grid">
          {filteredAdmins.map((admin) => (
            <div
              key={admin.id}
              className="admin-mgmt-card"
              onClick={() => openProfile(admin)}
            >
              <div className={`admin-mgmt-status ${admin.isActive !== false ? '' : 'inactive'}`}></div>
              <div className="admin-mgmt-card-content">
                <div className="admin-mgmt-avatar">
                  {getInitials(admin)}
                </div>
                <h3 className="admin-mgmt-name">{getCardDisplayName(admin)}</h3>
                <span className={`admin-mgmt-role ${admin.role || 'admin'}`}>
                  {getRoleDisplayName(admin.role)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profile Modal */}
      {profileModalOpen && selectedAdmin && (
        <div className="admin-mgmt-modal" onClick={closeProfile}>
          <div className="admin-mgmt-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-mgmt-modal-header">
              <button className="admin-mgmt-modal-close" onClick={closeProfile}>
                ×
              </button>
            </div>
            <div className="admin-mgmt-modal-body">
              {profileLoading ? (
                <div className="admin-mgmt-loading">
                  <div className="admin-mgmt-spinner"></div>
                </div>
              ) : (
                <>
                  <div className="admin-mgmt-profile-header">
                    <div className="admin-mgmt-profile-avatar">
                      {getInitials(selectedAdmin)}
                    </div>
                    <h3 className="admin-mgmt-profile-name">{getAdminDisplayName(selectedAdmin)}</h3>
                    <p className="admin-mgmt-profile-email">{selectedAdmin.email}</p>
                    <span className={`admin-mgmt-role ${selectedAdmin.role || 'admin'}`}>
                      {getRoleDisplayName(selectedAdmin.role)}
                    </span>
                  </div>

                  <div className="admin-mgmt-profile-details">
                    {profileData?.phone && (
                      <div className="admin-mgmt-profile-detail">
                        <span className="admin-mgmt-profile-detail-label">Phone</span>
                        <span className="admin-mgmt-profile-detail-value">{profileData.phone}</span>
                      </div>
                    )}
                    {profileData?.title && (
                      <div className="admin-mgmt-profile-detail">
                        <span className="admin-mgmt-profile-detail-label">Job Title</span>
                        <span className="admin-mgmt-profile-detail-value">{profileData.title}</span>
                      </div>
                    )}
                    {profileData?.department && (
                      <div className="admin-mgmt-profile-detail">
                        <span className="admin-mgmt-profile-detail-label">Department</span>
                        <span className="admin-mgmt-profile-detail-value">{profileData.department}</span>
                      </div>
                    )}
                    {profileData?.bio && (
                      <div className="admin-mgmt-profile-detail admin-mgmt-profile-detail-full">
                        <span className="admin-mgmt-profile-detail-label">Bio</span>
                        <span className="admin-mgmt-profile-detail-value admin-mgmt-profile-bio">{profileData.bio}</span>
                      </div>
                    )}
                    {profileData?.createdAt && (
                      <div className="admin-mgmt-profile-detail">
                        <span className="admin-mgmt-profile-detail-label">Member Since</span>
                        <span className="admin-mgmt-profile-detail-value">
                          {profileData.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="admin-mgmt-form-group">
                <label className="admin-mgmt-label">Role</label>
                <select
                  className="admin-mgmt-select"
                  value={editingRole}
                  onChange={(e) => setEditingRole(e.target.value)}
                  disabled={
                    selectedAdmin.email === currentAdmin?.email ||
                    selectedAdmin.role === 'super-admin'
                  }
                >
                  <option value="admin">Admin</option>
                  <option value="support">Support</option>
                  {selectedAdmin.role === 'super-admin' && (
                    <option value="super-admin" disabled>Super Admin</option>
                  )}
                </select>
                {selectedAdmin.role === 'super-admin' && (
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', margin: '6px 0 0 0' }}>
                    Super Admin role cannot be changed
                  </p>
                )}
              </div>

              <div className="admin-mgmt-modal-actions">
                {selectedAdmin.role !== 'support' && (
                  <button
                    className="admin-mgmt-btn admin-mgmt-btn-secondary"
                    onClick={handleSetAdminClaims}
                    disabled={settingClaims}
                  >
                    {settingClaims ? 'Setting...' : 'Set Admin Claims'}
                  </button>
                )}
                {editingRole !== selectedAdmin.role && selectedAdmin.role !== 'super-admin' && (
                  <button
                    className="admin-mgmt-btn admin-mgmt-btn-primary"
                    onClick={handleUpdateRole}
                  >
                    Save Role
                  </button>
                )}
                {isSuperAdmin && selectedAdmin.email !== currentAdmin?.email && (
                  <button
                    className="admin-mgmt-btn admin-mgmt-btn-danger"
                    onClick={() => handleDelete(selectedAdmin.id, selectedAdmin.email)}
                  >
                    Delete Account
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreateAdmin;
